import { useEffect, useRef, useCallback, useState } from 'react';
import { GenerationStatus } from '../generatorStore';

const SSE_RETRY_INTERVAL = 3000;
const SSE_MAX_RETRIES = 5;

interface UseSSEOptions {
  sessionId: string | null;
  onMessage: (status: GenerationStatus) => void;
  onError?: (err: Error) => void;
  onComplete?: () => void;
  onFallback?: () => void; // 当 SSE 不可用时降级到轮询
}

/**
 * SSE 实时推送 hook（P4-01）
 * - 自动连接 /api/ai/stream?session_id={id}
 * - 断线自动重连（最多 SSE_MAX_RETRIES 次）
 * - 失败后触发 onFallback 降级到轮询
 */
export function useSSE({
  sessionId,
  onMessage,
  onError,
  onComplete,
  onFallback,
}: UseSSEOptions) {
  const [connected, setConnected] = useState(false);
  const retryCountRef = useRef(0);
  const esRef = useRef<EventSource | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cleanup = useCallback(() => {
    if (esRef.current) {
      esRef.current.close();
      esRef.current = null;
    }
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    retryCountRef.current = 0;
    setConnected(false);
  }, []);

  useEffect(() => {
    if (!sessionId) return;

    const base = import.meta.env.VITE_API_BASE_URL || '';
    const url = `${base}/api/ai/stream?session_id=${sessionId}`;

    const connect = () => {
      try {
        const es = new EventSource(url);
        esRef.current = es;

        es.onopen = () => {
          retryCountRef.current = 0;
          setConnected(true);
        };

        es.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            if (data.type === 'status') {
              onMessage(data.payload as GenerationStatus);
              if (data.payload?.status === 'completed') {
                onComplete?.();
                cleanup();
              }
            } else if (data.type === 'error') {
              onError?.(new Error(data.message || 'SSE error'));
            }
          } catch (e) {
            onError?.(new Error('Invalid SSE message'));
          }
        };

        es.onerror = () => {
          es.close();
          esRef.current = null;
          setConnected(false);
          retryCountRef.current += 1;
          if (retryCountRef.current >= SSE_MAX_RETRIES) {
            onFallback?.();
            return;
          }
          timerRef.current = setTimeout(connect, SSE_RETRY_INTERVAL);
        };
      } catch (err) {
        onFallback?.();
      }
    };

    connect();
    return cleanup;
  }, [sessionId]);

  return { connected };
}
